import { Sparkline } from "@/components/Sparkline";
import { fmt, dirColor, countdown } from "@/lib/format";
import { LockedOverlay } from "@/components/LockedOverlay";
import { ArrowUpRight, ArrowDownRight, Clock, Lock } from "lucide-react";
import { InfoButton } from "@/components/IndicatorInfoDialog";

export function IndicatorCard({ ind, onClick }) {
  if (ind.locked) {
    return (
      <div data-testid={`indicator-card-${ind.code.toLowerCase()}`}
        className="glass rounded-2xl p-4 w-full flex flex-col gap-3 relative overflow-hidden min-h-[150px]">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-sm leading-tight truncate">{ind.name}</h3>
            <span className="text-[10px] font-mono-num uppercase tracking-wider text-[#6B7280]">{ind.code}</span>
          </div>
          <span className="text-[9px] font-mono-num px-1.5 py-0.5 rounded bg-[#F59E0B]/15 text-[#F59E0B] flex items-center gap-1"><Lock size={9} />PRO</span>
        </div>
        <div className="font-mono-num text-2xl font-bold blur-[6px] select-none">000.0</div>
        <LockedOverlay label="Unlock" />
      </div>
    );
  }

  const pred = ind.prediction;
  const up = ind.direction === "UP";
  const sparkColor = up ? "#10B981" : "#EF4444";
  const predUp = pred?.predicted_direction === "UP";

  return (
    <div
      role="button" tabIndex={0}
      data-testid={`indicator-card-${ind.code.toLowerCase()}`}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      className="glass hover-lift text-left rounded-2xl p-4 w-full flex flex-col gap-3 cursor-pointer"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex items-start gap-1">
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-sm leading-tight truncate">{ind.name}</h3>
            <span className="text-[10px] font-mono-num uppercase tracking-wider text-[#6B7280]">{ind.code}</span>
          </div>
          <InfoButton explain={ind.explain} testId={`info-${ind.code}`} />
        </div>
        <span className={`flex items-center gap-0.5 text-xs font-mono-num ${dirColor(ind.direction)}`}>
          {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {ind.net_change > 0 ? "+" : ""}{ind.net_change}
        </span>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono-num text-2xl font-bold tracking-tight">{fmt(ind.latest, ind.unit)}</div>
          <div className="text-[10px] text-[#6B7280] font-mono-num">prev {fmt(ind.previous, ind.unit)} · cons {fmt(ind.consensus, ind.unit)}</div>
          {ind.data_source && (
            <div data-testid={`source-${ind.code}`} className="text-[9px] text-[#4B5563] font-mono-num mt-0.5 truncate">
              {ind.data_source}{ind.consensus != null && ind.calendar_source ? ` · cons: ${ind.calendar_source}` : ""}
            </div>
          )}
        </div>
        <Sparkline data={ind.sparkline} color={sparkColor} width={84} height={34} />
      </div>

      {pred && (
        <div className="flex items-center justify-between pt-2 border-t border-white/5">
          <div className="flex items-center gap-1.5">
            <span
              className="text-[10px] font-bold font-mono-num px-1.5 py-0.5 rounded"
              style={{ color: predUp ? "#10B981" : "#EF4444", background: predUp ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)" }}
            >
              {pred.predicted_direction}
            </span>
            <span className="text-[10px] text-[#9CA3AF] font-mono-num">
              {fmt(pred.range_low, ind.unit)}–{fmt(pred.range_high, ind.unit)}
            </span>
          </div>
          <span className="text-[10px] font-mono-num text-[#F59E0B]">{Math.round(pred.probability * 100)}%</span>
        </div>
      )}

      <div className="flex items-center gap-1 text-[10px] text-[#6B7280] font-mono-num">
        <Clock size={11} /> next release {countdown(Math.max(0, Math.ceil((new Date(ind.next_release) - new Date()) / 86400000)))}
      </div>
    </div>
  );
}
